import { signalSessionLost, tryRefreshToken } from '~/lib/api-client';
import { timezoneHeader } from '~/lib/timezone';
import { UPLOAD_GENERIC_FAILURE, UPLOAD_INTERRUPTED } from './pro-builder-copy';

/** What the server answers for one saved file. */
export interface ProBuilderUploadResult {
  name: string;
  renamed: boolean;
  bytes: number;
}

/** A refused or broken upload; `message` is always a sentence a person can read. */
export class ProBuilderUploadError extends Error {
  constructor(
    message: string,
    public status: number,
  ) {
    super(message);
  }
}

/** The sentence to show for a non-200 answer: the server's own when it marked it for the person, else a generic one. */
export function uploadRefusalMessage(status: number, bodyText: string): string {
  if (status === 0) return UPLOAD_INTERRUPTED;
  try {
    const body = JSON.parse(bodyText) as { error?: unknown; userFacing?: unknown };
    if (body && body.userFacing === true && typeof body.error === 'string' && body.error) return body.error;
  } catch {
    // not JSON: fall through
  }
  return UPLOAD_GENERIC_FAILURE;
}

/** The slice of XMLHttpRequest this helper uses, so a test can stand in for it. */
export interface UploadXhr {
  open(method: string, url: string): void;
  setRequestHeader(name: string, value: string): void;
  send(body: FormData): void;
  upload: { onprogress: ((e: { lengthComputable: boolean; loaded: number; total: number }) => void) | null };
  onload: (() => void) | null;
  onerror: (() => void) | null;
  onabort: (() => void) | null;
  status: number;
  responseText: string;
}

export interface SendProBuilderDocumentParams {
  file: File;
  folder: string;
  onProgress: (percent: number) => void;
  /** Test seam; production uses the browser's XMLHttpRequest. */
  createXhr?: () => UploadXhr;
}

export function proBuilderDocumentsUrl(folder: string): string {
  return `/api/pro-builder/app/documents?folder=${encodeURIComponent(folder)}`;
}

const pendingUploads = new WeakMap<File, Map<string, string>>();

function sendOnce(p: SendProBuilderDocumentParams, requestId: string): Promise<{ status: number; text: string }> {
  return new Promise((resolve) => {
    const xhr = (p.createXhr ?? (() => new XMLHttpRequest() as unknown as UploadXhr))();
    xhr.open('POST', proBuilderDocumentsUrl(p.folder));
    xhr.setRequestHeader('Idempotency-Key', requestId);
    // Same headers api.form sends: the browser sets the multipart Content-Type itself, the timezone header rides along.
    for (const [k, v] of Object.entries(timezoneHeader())) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && e.total > 0) p.onProgress(Math.min(100, Math.round((e.loaded / e.total) * 100)));
    };
    xhr.onload = () => {
      resolve({ status: xhr.status, text: xhr.responseText });
    };
    xhr.onerror = () => resolve({ status: 0, text: '' });
    xhr.onabort = () => resolve({ status: 0, text: '' });
    const form = new FormData();
    // The server judges the exact name from this field (a browser percent-escapes `"` in a part's filename), so it goes BEFORE the file.
    form.append('name', p.file.name);
    form.append('file', p.file, p.file.name);
    xhr.send(form);
  });
}

/**
 * One multipart request per file, with byte progress (fetch cannot report upload progress, hence XHR).
 * Authenticated like `api.form` (same-origin cookie, timezone header), including its one silent
 * session refresh and retry on a 401.
 */
export async function sendProBuilderDocument(p: SendProBuilderDocumentParams): Promise<ProBuilderUploadResult> {
  let requests = pendingUploads.get(p.file);
  if (!requests) { requests = new Map(); pendingUploads.set(p.file, requests); }
  const requestId = requests.get(p.folder) ?? crypto.randomUUID();
  requests.set(p.folder, requestId);
  let answer = await sendOnce(p, requestId);
  if (answer.status === 401) {
    const renewed = await tryRefreshToken();
    if (renewed === 'ok') {
      p.onProgress(0);
      answer = await sendOnce(p, requestId);
    } else if (renewed === 'expired') {
      // Same as api.form: the session is gone, so the auth gate sends the person to sign in.
      signalSessionLost();
    }
  }
  if (answer.status === 200) {
    try {
      const body = JSON.parse(answer.text) as ProBuilderUploadResult;
      if (typeof body.name === 'string') {
        requests.delete(p.folder);
        p.onProgress(100);
        return { name: body.name, renamed: body.renamed === true, bytes: Number(body.bytes) || 0 };
      }
    } catch {
      // fall through to the generic failure
    }
    throw new ProBuilderUploadError(UPLOAD_GENERIC_FAILURE, 200);
  }
  try {
    const body = JSON.parse(answer.text) as { code?: unknown; userFacing?: unknown };
    if (answer.status === 409 && body.userFacing === true && body.code === 'upload_cancelled') requests.delete(p.folder);
  } catch {
    // An uncertain response retains the request key for recovery.
  }
  throw new ProBuilderUploadError(uploadRefusalMessage(answer.status, answer.text), answer.status);
}
